import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { useTable, useSortBy, useRowState } from 'react-table'
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdDone } from 'react-icons/md'

import BasicLgyInputField from 'app/components/util/lgy-input-field/BasicLgyInputField'
import BasicLgyTextArea from 'app/components/util/lgy-text-area/BasicLgyTextArea'
import LgyTooltip from 'app/components/util/lgy-tooltip/LgyTooltip'
import {formatDollars} from 'app/utils/fieldUtils'

import '../style/enterprisesettings.scss'

const EnterpriseSettings = ({ settings, handleSubmit }) => {
  const SubmitResetCell = useCallback(({ row: { index, state } }) => {
    return <span>
      <button className='usa-button' type='submit' title='Submit' aria-label='Submit' form={index} disabled={state.error}>
        <MdDone color='white' size='20' alt='Submit' />
      </button>
    </span>
  }, [])

  const EditableCell = ({ value: initialValue, row: { cells, index, original, state }, row, column: { Header, id } }) => {
    const [value, setValue] = useState('')
    const [error, setError] = useState('')

    const handleChange = (event) => {
      setValue(event.target.value)
      setError('')
      cells.find(cell => cell.column.id === id).setState(event.target.value)
    }

    const handleInvalid = (event) => {
      event.preventDefault()
      setError(`${event.target.title} is required`)
    }

    useEffect(() => {
      if (initialValue == null) {
        setValue('')
      } else if (id === 'value' && original.description.includes('Amount')) {
        setValue(formatDollars(initialValue))
      } else {
        setValue(initialValue)
      }
    }, [initialValue, original, id])

    // this is awkward, it would be preferable to use a pattern on input
    // but I was having difficulty getting a negative lookahead regex working
    useEffect(() => {
      if (id === 'note') { 
        if (state.cellState.value && !state.cellState.note) {
          setError('New note required')
          row.setState({ cellState: state.cellState, error: true })
        } else if (state.error && state.cellState.note) {
          row.setState({ cellState: state.cellState })
        }
      }
    }, [id, state.cellState, state.error, row])

    if (id === 'note' || id === 'value') {
      return <BasicLgyTextArea
        form={index}
        name={`${index}-${id}`}
        title={Header}
        value={value}
        onChange={handleChange}
        onInvalid={handleInvalid}
        error={error}
        maxLength='250'
        required />
    } else {
      return <BasicLgyInputField
        form={index}
        name={`${index}-${id}`}
        title={Header}
        value={value}
        onChange={handleChange}
        maxLength='100' />
    }
  }

  const columns = useMemo(() =>
    [{ Header: 'Description', accessor: 'description' },
    { Header: 'Value', accessor: 'value', Cell: EditableCell },
    { Header: 'Note', accessor: 'note', Cell: EditableCell },
    { Header: 'Application Area', accessor: 'applicationArea', Cell: EditableCell },
    { Header: 'Submit', Cell: SubmitResetCell }],
    [SubmitResetCell])

  const data = useMemo(() =>
    settings ? settings.map(setting => ({
      id: setting.id,
      name: setting.name,
      description: setting.description,
      value: setting.value,
      note: setting.note,
      applicationArea: setting.applicationArea
    })) : [],
    [settings])

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headerGroups,
    rows
  } = useTable({ columns, data }, useSortBy, useRowState)

  return (
    <div className='enterprise-settings'>
      <table {...getTableProps()} className='usa-table usa-table-borderless'>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  <span>
                  {column.render('Header')}
                  {column.Header === 'Submit' && <LgyTooltip message='Clicking the check box will update that individual row' id={column.id} />}
                  </span>
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? <MdKeyboardArrowDown size={18} alt='sorted descending' />
                        : <MdKeyboardArrowUp size={18} alt='sorted ascending' />
                      : ''}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                <td hidden><form id={row.index} onSubmit={(event) => handleSubmit(event, { ...row.original, ...row.state.cellState })} /></td>
                {row.cells.map(cell => (
                  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default EnterpriseSettings
